import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Lottie from "lottie-react";
import searchAnimation from "../../Assets/Loader/search.json";
import {
  FiMail,
  FiEye,
  FiEyeOff,
  FiCheck,
  FiX,
  FiShield,
} from "react-icons/fi";
import { checkEmail, login, sendOtp } from "../../Services/AuthenticationService";
import DriftWall from "../../Components/SignIn/DriftWall";
import { APPRoutes } from "../../Utils/Route";
import { toast } from "react-toastify";
import { useAuth } from "../../Context/AuthContext";
import { useNavigate } from "react-router-dom";

// Product screenshots that drift behind the login card. Loaded in bulk from
// the pre-compressed Driftwall_images folder so the background stays lightweight.
const picsContext = require.context("../../Assets/Images/Driftwall_images", false, /\.jpe?g$/);
const DRIFT_WALL_ITEMS = picsContext.keys().map((key) => ({ image: picsContext(key) }));

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [emailCheckState, setEmailCheckState] = useState({
    status: "idle",
    checkedEmail: "",
  });
  const [loginState, setLoginState] = useState("idle"); // idle | loading | success | error
  const emailCheckRequestId = useRef(0);
  const { persistPermissions, persistMenuItems } = useAuth();
  const navigate = useNavigate();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      password: "",
      is_login: true
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .trim()
        .required("Username is required")
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Enter a valid email address"),
      password: Yup.string()
        .required("Password is required")
    }),
    onSubmit: async (values) => {
      setLoginState("loading");
      setEmailCheckState({
        status: "loading",
        checkedEmail: values.username,
      });

      try {
        const response = await login(values);

        if (!response?.is_success) {
          setLoginState("error");
          setEmailCheckState((prev) => ({
            ...prev,
            status: "error",
          }));
          if (response?.is_success === false && response?.status_code === 500) {
            toast.error("Internal Server Error. Please Contact Support Team.");
          } else {
            toast.error(response?.message || "Login failed. Please check your credentials and try again.");
          }
          return;
        }

        const [otpResponse, emailCheckResponse] = await Promise.all([
          sendOtp(values.username),
          checkEmail(values.username),
        ]);

        if (emailCheckResponse?.is_success) {
          const permissions = Array.isArray(emailCheckResponse?.data?.Permissions) ? emailCheckResponse.data.Permissions : [];
          const menuItems = Array.isArray(emailCheckResponse?.data?.MenuItems) ? emailCheckResponse.data.MenuItems : [];

          if (permissions.length) {
            persistPermissions(permissions);
          }
          if (menuItems.length) {
            persistMenuItems(menuItems);
          }

          setEmailCheckState({
            status: "success",
            checkedEmail: values.username,
          });
        } else {
          setEmailCheckState({
            status: "error",
            checkedEmail: values.username,
          });
        }

        if (otpResponse?.is_success) {
          sessionStorage.setItem("mfa_pending_username", values.username);
          const expiry = Date.now() + 30 * 1000;
          sessionStorage.setItem(`mfa_otp_expiry_${values.username}`, expiry.toString());
          setLoginState("success");
          navigate(APPRoutes.MFA_VERIFICATION, {
            state: { username: values.username },
          });
        } else {
          setLoginState("error");
          toast.error(otpResponse?.message || "Failed to send OTP. Please try again.");
        }
      } catch (error) {
        setLoginState("error");
        setEmailCheckState((prev) => ({
          ...prev,
          status: prev.status === "success" ? "success" : "error",
        }));
        toast.error(error?.message || "An unexpected error occurred. Please try again later.");
        console.error("Login error:", error);
      }
    },
  });

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleUsernameChange = (event) => {
    formik.handleChange(event);
    emailCheckRequestId.current += 1;

    setEmailCheckState({
      status: "idle",
      checkedEmail: "",
    });
  };

  const handlePasswordFocus = async () => {
    const email = formik.values.username.trim();

    formik.setFieldTouched("username", true, true);

    if (!email || !isValidEmail(email)) return;
    if (emailCheckState.status === "loading") return;
    if (emailCheckState.checkedEmail === email) return;

    const requestId = ++emailCheckRequestId.current;

    setEmailCheckState({
      status: "loading",
      checkedEmail: email,
    });

    try {
      const response = await checkEmail(email);
      if (response?.is_success) {
        const permissions = Array.isArray(response?.data?.Permissions) ? response.data.Permissions : [];
        const menuItems = Array.isArray(response?.data?.MenuItems) ? response.data.MenuItems : [];

        if (permissions.length) {
          persistPermissions(permissions);
        }
        if (menuItems.length) {
          persistMenuItems(menuItems);
        }

        if (emailCheckRequestId.current !== requestId) return;
        setEmailCheckState({
          status: "success",
          checkedEmail: email,
        });
      } else if (response?.is_success === false && response?.status_code === 500) {
        setEmailCheckState({
          status: "error",
          checkedEmail: email,
        });
        toast.error("Internal Server Error. Please Contact Support Team.");
      } else {
        setEmailCheckState({
          status: "error",
          checkedEmail: email,
        });
        toast.error(response?.message || "Failed to verify email. Please try again.");
      }
    } catch (error) {
      if (emailCheckRequestId.current !== requestId) return;
      setEmailCheckState({
        status: "error",
        checkedEmail: email,
      });
      toast.error("An unexpected error occurred. Please try again later.");
      console.error("Error checking email", error);
    }
  };

  const getFieldError = (fieldName) => formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : "";

  const renderLoginButtonContent = () => {
    if (loginState === "loading") {
      return (
        <Lottie
          animationData={searchAnimation}
          loop={true}
          style={{ width: "3.5rem", height: "3.5rem" }}
        />
      );
    }
    return "Sign In";
  };

  const renderEmailStatusIndicator = () => {
    if (emailCheckState.status === "loading") {
      return (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          style={{ width: "1rem", height: "1rem", color: "inherit" }}
        ></span>
      );
    }

    if (emailCheckState.status === "success") {
      return (
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle"
          style={{
            width: "1.2rem",
            height: "1.2rem",
            backgroundColor: "var(--hc-success-green)",
            color: "var(--hc-white)",
          }}
        >
          <FiCheck size={20} />
        </span>
      );
    }

    if (emailCheckState.status === "error") {
      return (
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle"
          style={{
            width: "1.2rem",
            height: "1.2rem",
            backgroundColor: "var(--hc-auth-form-danger)",
            color: "var(--hc-white)",
          }}
        >
          <FiX size={20} />
        </span>
      );
    }

    return <FiMail size={20} />;
  };

  return (
    <div data-bs-theme="light" className="hc-auth-shell position-relative min-vh-100 w-100 overflow-hidden">
      <div className="hc-auth-driftwall-wrap position-absolute">
        <DriftWall items={DRIFT_WALL_ITEMS} fade={0.05} />
      </div>

      <div className="hc-auth-center position-relative min-vh-100 w-100 d-flex align-items-center justify-content-center pe-none">
        <div className="hc-auth-panel position-relative w-100 d-flex flex-column justify-content-center pe-auto overflow-hidden">
          <div className="hc-auth-header d-flex flex-column align-items-start">
            <div className="hc-auth-hero-pill align-self-start d-inline-flex align-items-center py-1 px-3 rounded-pill text-nowrap pe-none">
              <FiShield size={14} className="flex-shrink-0" />
              <span>Secure connection</span>
            </div>

            <div className="hc-auth-header-row d-flex align-items-center w-100">
              <img
                src="https://www.aegishs.us/aegis-img/badge_logo_ring.png"
                alt="Aegis HS 360"
                className="hc-auth-logo object-fit-contain"
              />
              <div className="hc-auth-header-text flex-fill">
                <h2 className="hc-auth-title text-start m-0">Welcome <span className="hc-auth-title-accent">Back</span>&nbsp;!</h2>
                <p className="hc-auth-subtitle text-start mt-1 mx-0 mb-0">Please sign in to continue</p>
              </div>
            </div>
          </div>

          <div className="hc-auth-panel-inner">
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className="hc-auth-field">
                <label htmlFor="username" className="hc-auth-field-label d-block fs-6">
                  Email
                </label>
                <div className={`hc-auth-underline d-flex align-items-center gap-2${getFieldError("username") ? " is-invalid" : ""}`}>
                  <input
                    name="username"
                    id="username"
                    type="text"
                    className="hc-auth-input border-0 rounded-0 shadow-none fw-bold p-0"
                    placeholder="you@example.com"
                    autoComplete="username"
                    value={formik.values.username}
                    onChange={handleUsernameChange}
                    onBlur={formik.handleBlur}
                    aria-invalid={Boolean(getFieldError("username"))}
                  />
                  <span className="hc-auth-field-icon d-flex align-items-center border-0 p-0">{renderEmailStatusIndicator()}</span>
                </div>
                <div className="hc-auth-field-error">{getFieldError("username")}</div>
              </div>

              <div className="hc-auth-field">
                <div className="hc-auth-row-between d-flex align-items-center justify-content-between">
                  <label htmlFor="password" className="hc-auth-field-label mb-0 fs-6">
                    Password
                  </label>
                  <Link to={APPRoutes.FORGOT_PASSWORD} className="hc-auth-forgot-link text-decoration-none">
                    Forgot password?
                  </Link>
                </div>
                <div className={`hc-auth-underline d-flex align-items-center gap-2${getFieldError("password") ? " is-invalid" : ""}`}>
                  <input
                    name="password"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className="hc-auth-input border-0 rounded-0 shadow-none fw-bold p-0"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={{ letterSpacing: "0.15rem" }}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    onFocus={handlePasswordFocus}
                    aria-invalid={Boolean(getFieldError("password"))}
                  />
                  <button
                    type="button"
                    className="hc-auth-field-icon d-flex align-items-center border-0 p-0"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
                <div className="hc-auth-field-error">{getFieldError("password")}</div>
              </div>

              <button
                type="submit"
                className={`btn hc-auth-submit-btn rounded-pill mt-2 w-100 fw-semibold border-0 fs-5 d-flex align-items-center justify-content-center ${loginState === "loading" ? "p-0" : "py-2"}`}
                disabled={emailCheckState.status === "loading" || loginState === "loading" || loginState === "success"}
                style={{
                  color: "var(--hc-white)",
                  boxShadow: "0 14px 32px rgba(var(--hc-black-rgb), 0.25)",
                  opacity: emailCheckState.status === "loading" || loginState === "loading" ? 0.7 : 1,
                  minHeight: "3rem",
                }}
              >
                {renderLoginButtonContent()}
              </button>
            </form>

            <p className="mt-3 mb-0 text-center fs-6 hc-auth-footer-text">
              Don't have an account?
              <a href="https://www.aegishs.us/contact.php" target="_blank" rel="noopener noreferrer" className="ms-1 text-decoration-none text-nowrap hc-auth-footer-link fw-bold">
                Request Access
              </a>
            </p>

            <p className="hc-auth-trust-line d-flex align-items-center justify-content-center text-nowrap">
              <FiShield size={13} />
              Secured with encryption &middot; HIPAA compliant
            </p>
            </div>
          </div>
        </div>
      </div>
  );
};

export default LoginPage;